import { useDateFormat } from './useDateFormat'

export interface NewsGalleryItem {
    type: 'image' | 'video'
    url: string
    description: string
}

export interface NewsItem {
    id: string
    title: string
    excerpt: string
    content?: string
    date: string
    /** ISO date as served by the API (`date` is formatted for display) */
    dateRaw?: string
    category: string
    image: string
    slug: string
    linkedEvents?: any[] // Simplified for now
    gallery?: NewsGalleryItem[]
    metaTitle?: string
    metaDescription?: string
    keywords?: string
    ogImage?: string
    noIndex?: boolean
}

export const useNews = () => {
    const config = useRuntimeConfig()
    const apiBase = config.public.apiBase
    const { formatDayMonthYear } = useDateFormat()

    const getAllNews = async (page = 1, pageSize = 12, year?: number): Promise<{ items: NewsItem[], totalPages: number }> => {
        let url = `${apiBase}/oxoweb/news?page=${page}&pageSize=${pageSize}`
        if (year) url += `&year=${year}`

        const { data, error } = await useFetch<any>(url)
        if (error.value) {
            console.error('Error fetching news:', error.value)
            return { items: [], totalPages: 0 }
        }

        const items = (data.value?.items || []).map((item: any) => ({
            ...item,
            date: formatDayMonthYear(item.date)
        }))

        return {
            items,
            totalPages: data.value?.totalPages || 0
        }
    }

    const getAllNewsYears = async (): Promise<number[]> => {
        const { data, error } = await useFetch<number[]>(`${apiBase}/oxoweb/news/years`)
        if (error.value) {
            console.error('Error fetching news years:', error.value)
            return [new Date().getFullYear()]
        }
        return data.value || []
    }

    const getLatestNews = async (limit: number = 3): Promise<NewsItem[]> => {
        const all = (await getAllNews()).items
        return all.slice(0, limit)
    }

    const getNewsBySlug = async (slug: string): Promise<NewsItem | undefined> => {
        const { data, error } = await useFetch(`${apiBase}/oxoweb/news/${slug}`)
        if (error.value) {
            console.error(`Error fetching news ${slug}:`, error.value)
            return undefined
        }
        const item = data.value as any
        if (!item) return undefined

        return {
            ...item,
            date: formatDayMonthYear(item.date),
            dateRaw: item.date,
            linkedEvents: (item.linkedEvents || []).map((e: any) => ({
                ...e,
                fullDate: e.date,
                date: formatDayMonthYear(e.date)
            }))
        }
    }

    const getNewsCategories = (items: NewsItem[]) => ['Toutes', ...new Set(items.map(n => n.category))]

    return {
        getAllNews,
        getAllNewsYears,
        getLatestNews,
        getNewsBySlug,
        getNewsCategories
    }
}
